import React, { useEffect, useMemo, useState } from "react";
import { logError } from "../lib/log";
import { api, apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { GraduationCap } from "lucide-react";
import { YearTabs, CoverageHeatmap, UnitSection } from "../components/CurriculumParts";

const EMPTY = new Set();

export default function Curriculum() {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(1);
  const [progress, setProgress] = useState(null);
  const [err, setErr] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/curriculum").then((r) => setData(r.data)).catch((e) => setErr(apiError(e, "Could not load the curriculum.")));
  }, []);
  useEffect(() => {
    if (!user) return setProgress(null);
    api.get("/curriculum/progress").then((r) => setProgress(r.data)).catch((e) => logError("Progress load failed", e));
  }, [user]);

  const activeYear = useMemo(() => data?.years.find((y) => y.year === year), [data, year]);
  const readByYear = useMemo(() => Object.fromEntries((progress?.by_year || []).map((y) => [y.year, new Set(y.read)])), [progress]);

  if (err) return <div className="rounded-md border border-red-400/40 bg-red-500/10 p-4 text-sm text-red-200" data-testid="curriculum-error">{err}</div>;
  if (!data) return <div className="text-slate-400">Loading curriculum…</div>;
  const readSet = readByYear[year] || EMPTY;
  const py = progress?.by_year.find((p) => p.year === year);

  return (
    <div className="space-y-8" data-testid="curriculum-root">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1 text-xs uppercase tracking-widest text-cyan-300">
          <GraduationCap size={12} /> IB Bilingual Curriculum
        </div>
        <h1 className="font-display mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Slovak IB Chemistry · 3-Year Syllabus</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-400">
          Every lesson generates a concise explainer grounded in <em>Chemistry for the IB Diploma 3e (Talbot)</em>. Tap any lesson to open.
        </p>
      </div>

      <YearTabs years={data.years} summary={data.summary} progress={progress} year={year} onSelect={setYear} />

      <div className="space-y-6">
        <CoverageHeatmap year={year} units={activeYear.units} readSet={readSet} py={py} />
        {activeYear.units.map((u, ui) => <UnitSection key={u.unit} year={year} unit={u} index={ui} readSet={readSet} />)}
      </div>
    </div>
  );
}
